import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmButton, Pill, Segmented } from "./mailingUi";
import useMailTranslator, { OTHER_LANGUAGE, readAutoTranslate, storeAutoTranslate } from "./useMailTranslator";

export const PLACEHOLDERS = ['name', 'email', 'environment', 'environmentUrl', 'expiryDate', 'daysLeft', 'trialDays'];

const FIELDS = [
  { key: 'subject', rows: 0, required: true, counter: 70 },
  { key: 'header', rows: 2, format: true },
  { key: 'body', rows: 9, required: true, format: true },
  { key: 'buttonText', rows: 0, counter: 30 },
  { key: 'buttonUrl', rows: 0 },
  { key: 'outro', rows: 4, format: true },
];

const LANGUAGES = [{ value: 'nl', label: 'Nederlands' }, { value: 'en', label: 'English' }];

const sameText = (a, b) => LANGUAGES.every(({ value: lang }) => FIELDS.every(({ key }) => (a?.[lang]?.[key] || '') === (b?.[lang]?.[key] || '')));

/**
 * Editor for a bilingual mailing: { en: {subject, header, body, buttonText, buttonUrl, outro}, nl: {...} }
 * `onChange` receives an updater `(prev) => next`, so late AI translations are merged into the newest text.
 */
const MailingContentEditor = ({ content, onChange, showTrialBlock = false, language, onLanguageChange, defaultContent }) => {
  const { t } = useTranslation();
  const [localLanguage, setLocalLanguage] = useState('nl');
  const activeLanguage = language || localLanguage;
  const setActiveLanguage = onLanguageChange || setLocalLanguage;
  const otherLanguage = OTHER_LANGUAGE[activeLanguage];
  const [autoTranslate, setAutoTranslate] = useState(readAutoTranslate);
  const translator = useMailTranslator({ content, onChange, autoTranslate });
  const fieldRefs = useRef({});
  const lastField = useRef('body');

  const languageName = (lang) => t(`mailing.lang_${lang}`);
  const isFilled = (lang) => Boolean(content?.[lang]?.subject?.trim() && content?.[lang]?.body?.trim());
  const hasText = (lang) => FIELDS.some(({ key }) => key !== 'buttonUrl' && content?.[lang]?.[key]?.trim());
  const isDefault = defaultContent && sameText(content, defaultContent);
  const otherReview = Object.keys(translator.aiFields[otherLanguage]).length;

  const handleChange = (field, value) => {
    const lang = activeLanguage;
    onChange((prev) => ({ ...prev, [lang]: { ...prev?.[lang], [field]: value } }));
    translator.markEdited(lang, field);
  };

  // Replace the selection of the field that was focused last and select `select` within the inserted text
  const replaceSelection = (field, build) => {
    const element = fieldRefs.current[field];
    const current = content?.[activeLanguage]?.[field] || '';
    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    const { text, selectFrom, selectTo } = build(current.slice(start, end));
    handleChange(field, current.slice(0, start) + text + current.slice(end));
    requestAnimationFrame(() => {
      if (!element) return;
      element.focus();
      element.setSelectionRange(start + selectFrom, start + selectTo);
    });
  };

  const insertPlaceholder = (placeholder) => replaceSelection(lastField.current, () => {
    const token = `{{${placeholder}}}`;
    return { text: token, selectFrom: token.length, selectTo: token.length };
  });

  const makeBold = (field) => replaceSelection(field, (selected) => {
    const inner = selected || t('mailing.format_bold_text');
    return { text: `**${inner}**`, selectFrom: 2, selectTo: 2 + inner.length };
  });

  const makeLink = (field) => replaceSelection(field, (selected) => {
    const label = selected || t('mailing.format_link_text');
    const url = 'https://';
    const text = `[${label}](${url})`;
    // With selected text the URL still has to be typed, otherwise the link text
    return selected
      ? { text, selectFrom: label.length + 3, selectTo: label.length + 3 + url.length }
      : { text, selectFrom: 1, selectTo: 1 + label.length };
  });

  const toggleAutoTranslate = () => {
    storeAutoTranslate(!autoTranslate);
    setAutoTranslate(!autoTranslate);
  };

  const reset = () => {
    translator.clear();
    onChange(() => defaultContent);
  };

  const dotState = (lang) => {
    if (translator.translatingTo === lang) return { className: 'busy', title: t('mailing.translating_to', { language: languageName(lang) }) };
    if (Object.keys(translator.aiFields[lang]).length) return { className: 'review', title: t('mailing.language_review') };
    if (isFilled(lang)) return { className: 'ok', title: t('mailing.language_complete') };
    return { className: '', title: t('mailing.language_incomplete') };
  };

  const renderTranslateStatus = () => {
    if (translator.translatingTo) {
      return <span className="mail-translate-status busy"><i className="icon-refresh" /> {t('mailing.translating_to', { language: languageName(translator.translatingTo) })}</span>;
    }
    if (translator.error) {
      return (
        <span className="mail-translate-status error">
          <i className="icon-alert" /> {t('mailing.translate_error', { error: translator.error.message || t('mailing.error') })}
          <button type="button" className="mail-link-btn" onClick={translator.retry}>{t('mailing.retry')}</button>
        </span>
      );
    }
    if (otherReview > 0) {
      return (
        <button type="button" className="mail-translate-status review" onClick={() => setActiveLanguage(otherLanguage)}>
          <i className="icon-view" /> {t('mailing.translate_review', { count: otherReview, language: languageName(otherLanguage) })}
        </button>
      );
    }
    return (
      <span className="mail-translate-status">
        {autoTranslate ? t('mailing.auto_translate_hint', { language: languageName(otherLanguage) }) : t('mailing.auto_translate_off_hint')}
      </span>
    );
  };

  return (
    <div className="mail-editor">
      <div className="mail-editor-bar">
        <Segmented
          ariaLabel={t('mailing.language')}
          value={activeLanguage}
          onChange={setActiveLanguage}
          options={LANGUAGES.map((lang) => {
            const dot = dotState(lang.value);
            return {
              value: lang.value,
              label: (
                <>
                  <span className={`mail-seg-dot ${dot.className}`} title={dot.title} />
                  {lang.label}
                </>
              ),
            };
          })}
        />
        {defaultContent && (
          <div className="mail-draft-state">
            <Pill tone={isDefault ? 'gray' : 'blue'} title={isDefault ? t('mailing.draft_default_help') : t('mailing.draft_edited_help')}>
              {isDefault ? t('mailing.draft_default') : t('mailing.draft_edited')}
            </Pill>
            {!isDefault && (
              <ConfirmButton icon="icon-undo" label={t('mailing.reset_default')} confirmText={t('mailing.reset_confirm')} confirmLabel={t('mailing.reset')} onConfirm={reset} />
            )}
          </div>
        )}
      </div>

      <div className="mail-translate-bar">
        <span className="mail-translate-icon"><i className="icon-stars1" /></span>
        <button type="button" role="switch" aria-checked={autoTranslate} className="mail-translate-switch" onClick={toggleAutoTranslate}>
          <span className={`mail-switch small ${autoTranslate ? 'on' : ''}`} />
          {t('mailing.auto_translate')}
        </button>
        <div className="mail-translate-text">{renderTranslateStatus()}</div>
        <ConfirmButton
          className="mail-icon-btn"
          icon="icon-globe"
          label={t('mailing.translate_all', { language: languageName(otherLanguage) })}
          disabled={!hasText(activeLanguage) || Boolean(translator.translatingTo)}
          needsConfirm={hasText(otherLanguage)}
          confirmText={t('mailing.translate_all_confirm', { language: languageName(otherLanguage) })}
          confirmLabel={t('mailing.translate')}
          onConfirm={() => translator.translateAll(activeLanguage)}
        />
      </div>

      {FIELDS.map(({ key, rows, required, counter, format }) => {
        const value = content?.[activeLanguage]?.[key] || '';
        const aiMissing = translator.aiFields[activeLanguage][key];
        const props = {
          id: `mailing-${key}`,
          className: `form-control ${aiMissing ? 'mail-ai-field' : ''}`,
          value,
          onChange: (e) => handleChange(key, e.target.value),
          onFocus: () => { lastField.current = key; },
          ref: (element) => { fieldRefs.current[key] = element; },
        };
        return (
          <div className="form-group" key={key}>
            <div className="mail-field-head">
              <label htmlFor={`mailing-${key}`}>
                {t(`mailing.field_${key}`)}{required && <span className="mail-required"> *</span>}
              </label>
              {aiMissing && (
                <span className="mail-ai-badge" title={t('mailing.ai_translated_help')}><i className="icon-stars1" /> {t('mailing.ai_translated')}</span>
              )}
              <span className="mail-field-tools">
                {format && (
                  <>
                    <button type="button" className="mail-format-btn" title={t('mailing.format_bold')} aria-label={t('mailing.format_bold')} onMouseDown={(e) => e.preventDefault()} onClick={() => makeBold(key)}><b>B</b></button>
                    <button type="button" className="mail-format-btn" title={t('mailing.format_link')} aria-label={t('mailing.format_link')} onMouseDown={(e) => e.preventDefault()} onClick={() => makeLink(key)}><i className="icon-link" /></button>
                  </>
                )}
                {counter && (
                  <span className={`mail-counter ${value.length > counter ? 'warn' : ''}`} title={t(`mailing.length_help_${key}`, { count: counter })}>
                    {value.length}
                  </span>
                )}
              </span>
            </div>
            {rows > 0
              ? <textarea rows={rows} {...props} />
              : <input type="text" placeholder={key === 'buttonUrl' ? '{{environmentUrl}}' : ''} {...props} />}
            {aiMissing?.length > 0 && (
              <div className="mail-field-warning"><i className="icon-alert" /> {t('mailing.translate_missing', { tokens: aiMissing.join(', ') })}</div>
            )}
          </div>
        );
      })}

      <div className="mail-placeholders">
        <div className="mail-placeholders-title">{t('mailing.placeholders_insert')}</div>
        <div className="mail-placeholder-chips">
          {PLACEHOLDERS.map((placeholder) => (
            <button key={placeholder} type="button" className="mail-placeholder-chip" onMouseDown={(e) => e.preventDefault()} onClick={() => insertPlaceholder(placeholder)}>
              {`{{${placeholder}}}`}
            </button>
          ))}
        </div>
        <ul className="mail-tips">
          <li>{t('mailing.formatting_help')}</li>
          {showTrialBlock && <li>{t('mailing.trial_block_help')}</li>}
          <li>{t('mailing.language_fallback_help')}</li>
        </ul>
      </div>
    </div>
  );
};

export default MailingContentEditor;
